import { config } from '../config';
import { authorizedFetch } from './auth';

/**
 * Chat transport.
 *
 * The conversation is stateless server-side: the full history travels with every
 * request, and the backend streams back both the visible tokens and the exact
 * messages to append locally. That keeps the Lambda free of session storage, at
 * the cost of a payload that grows with the conversation.
 *
 * EventSource cannot POST, so SSE frames are parsed off a fetch body stream here.
 */

export type ContentBlock = { type: string; [key: string]: unknown };

export type ChatMessage = {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
};

export type PendingConfirmation = {
  toolUseId: string;
  name: string;
  input: unknown;
};

export type ChatEvents = {
  onTextDelta(text: string): void;
  onThinking(): void;
  onToolStart(toolUseId: string, name: string): void;
  onToolResult(result: {
    toolUseId: string;
    name: string;
    isError: boolean;
    declined: boolean;
    text: string;
  }): void;
  onConfirmationRequired(confirmation: PendingConfirmation): void;
  onAppend(message: ChatMessage): void;
  onError(message: string): void;
  onDone(): void;
};

export type SendOptions = {
  messages: ChatMessage[];
  approvals?: string[];
  declines?: Array<{ tool_use_id: string; reason?: string }>;
  signal?: AbortSignal;
};

type ServerEvent =
  | { event: 'text_delta'; data: { text: string } }
  | { event: 'thinking_delta'; data: { text: string } }
  | { event: 'tool_start'; data: { tool_use_id: string; name: string } }
  | {
      event: 'tool_result';
      data: {
        tool_use_id: string;
        name: string;
        is_error?: boolean;
        declined?: boolean;
        text: string;
      };
    }
  | {
      event: 'confirmation_required';
      data: { tool_use_id: string; name: string; input: unknown };
    }
  | { event: 'append'; data: { message: ChatMessage } }
  | { event: 'error'; data: { message: string } }
  | { event: 'done'; data: unknown };

function dispatch(frame: ServerEvent, handlers: ChatEvents): void {
  switch (frame.event) {
    case 'text_delta':
      handlers.onTextDelta(frame.data.text);
      break;
    case 'thinking_delta':
      handlers.onThinking();
      break;
    case 'tool_start':
      handlers.onToolStart(frame.data.tool_use_id, frame.data.name);
      break;
    case 'tool_result':
      handlers.onToolResult({
        toolUseId: frame.data.tool_use_id,
        name: frame.data.name,
        isError: frame.data.is_error === true,
        declined: frame.data.declined === true,
        text: frame.data.text,
      });
      break;
    case 'confirmation_required':
      handlers.onConfirmationRequired({
        toolUseId: frame.data.tool_use_id,
        name: frame.data.name,
        input: frame.data.input,
      });
      break;
    case 'append':
      handlers.onAppend(frame.data.message);
      break;
    case 'error':
      handlers.onError(frame.data.message);
      break;
    case 'done':
      handlers.onDone();
      break;
  }
}

/** Splits an SSE frame into its event name and JSON payload. */
function parseFrame(raw: string): ServerEvent | null {
  let name = 'message';
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) {
      continue; // keep-alive comment
    }
    if (line.startsWith('event:')) {
      name = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  try {
    return { event: name, data: JSON.parse(dataLines.join('\n')) } as ServerEvent;
  } catch {
    return null;
  }
}

export class ApiKeyMissingError extends Error {}

export async function sendTurn(options: SendOptions, handlers: ChatEvents): Promise<void> {
  const response = await authorizedFetch(config().chatUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages: options.messages,
      approvals: options.approvals,
      declines: options.declines,
    }),
    signal: options.signal,
  });

  if (!response.ok || !response.body) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string; code?: string };
      if (body.code === 'api_key_missing') {
        throw new ApiKeyMissingError(body.error ?? 'Geen MyParcel API key ingesteld');
      }
      detail = body.error ?? detail;
    } catch (error) {
      if (error instanceof ApiKeyMissingError) {
        throw error;
      }
    }
    throw new Error(detail);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += value;

    // Frames are separated by a blank line; anything after the last separator is
    // an incomplete frame and stays in the buffer.
    let separator = buffer.indexOf('\n\n');
    while (separator !== -1) {
      const frame = parseFrame(buffer.slice(0, separator));
      buffer = buffer.slice(separator + 2);
      if (frame) {
        dispatch(frame, handlers);
      }
      separator = buffer.indexOf('\n\n');
    }
  }

  const trailing = buffer.trim() ? parseFrame(buffer) : null;
  if (trailing) {
    dispatch(trailing, handlers);
  }
}
